import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyLogFloodgateActivity, SurveyActivityType } from 'owa-floodgate-feedback-view';
import { lazyTriggerResizeEvent } from 'owa-resize-event';
import { lazyResetPreviewPane, PreviewPaneMode } from 'owa-sxsdata';
import { orchestrator } from 'satcheljs';
import {
    internalCloseSxS,
    onComposeStateChange,
    onInvisibilityChange,
    onShouldHideReadingPaneOutsideSxSChange,
    onShouldUseFakeRightHandSideChange,
    onUsingFakeRightHandSideChange,
} from '../actions/internalActions';
import { onSxSChangeOrClose, reloadReadingPaneAfterClosingSxS } from '../actions/publicActions';
import { forceCloseSxS, onSxSReadingPaneChange } from '../actions/publicLazyActions';
import PreviewPaneSelectionSource from '../store/schema/PreviewPaneSelectionSource';
import { SxSReadingPaneMode } from '../store/schema/SxSReadingPaneState';
import SxSStore from '../store/schema/SxSStore';
import sxsStore from '../store/Store';
import { AnimationEndEvent, CloseEvent } from '../utils/eventNames';
import logCloseDatapoint from '../utils/logCloseDatapoint';
import openComposeViewStateInSecondaryTabHelper from '../utils/openComposeViewStateInSecondaryTabHelper';
import * as repeatTracker from '../utils/repeatTracker';

function sleep(t: number) {
    return new Promise(resolve => setTimeout(resolve, t));
}

async function logFloodgateActivity(surveyActivityType: SurveyActivityType) {
    const logFloodgateActivity = await lazyLogFloodgateActivity.import();
    logFloodgateActivity(surveyActivityType);
}

export async function forceCloseSxSOrchestrator(sxsStore: SxSStore, closingSource: string) {
    const sessionLength = new Date().getTime() - sxsStore.sessionStartTime;
    const sessionPreviewCount = sxsStore.sessionPreviewCount;
    logCloseDatapoint(closingSource, sessionLength, sessionPreviewCount);

    // disposal of inline compose in SxS and initialization in mail reading pane
    // can cause a race condition that will crash OWA
    // Here is a workaround to remove the active reading pane in SxS first
    // And then use sleep before letting mail reading pane to re-render
    const needToReloadReadingPane =
        sxsStore.readingPaneState &&
        sxsStore.readingPaneState.mode === SxSReadingPaneMode.ConversationReadingPane;

    // Calling this will make SxSV2 make a copy of static HTML of reading pane
    onShouldUseFakeRightHandSideChange(true);
    await sleep(1);

    // Calling this will make SxSV2 to use the copy of static HTML instead of an active reading pane
    onUsingFakeRightHandSideChange(true);
    await sleep(1);

    // Now we can tell mail module to re-render reading pane
    // because at this time there is no active reading pane in SxS
    onShouldHideReadingPaneOutsideSxSChange(false);
    onSxSReadingPaneChange(null);
    needToReloadReadingPane && reloadReadingPaneAfterClosingSxS();

    // if compose is opened in SxS, put it back to a tab in reading pane
    const composeId = sxsStore.composeId;
    if (composeId) {
        onComposeStateChange(null);
        openComposeViewStateInSecondaryTabHelper.run(composeId);
    }

    await sleep(1);

    onInvisibilityChange(true);
    const waitForAnimation = new Promise(resolve => {
        sxsStore.eventManager.once(AnimationEndEvent, () => {
            resolve();
        });
    });

    // wait for the animation end event - the animation is 0.367s
    // if the animation event does not come in 0.5s then just proceed
    await Promise.race([waitForAnimation, sleep(500)]);

    const isWacAttachment: boolean =
        sxsStore.viewState?.previewPane &&
        sxsStore.viewState.previewPane.mode === PreviewPaneMode.Wac;

    const isCloudyAttachment: boolean = sxsStore.extendedViewState?.isCloudy;

    const shouldShowAttachmentSurvey =
        isFeatureEnabled('doc-SxS-jsApi-ODB-Base') ||
        isFeatureEnabled('doc-SxS-jsApi-ODB-BaseNoEditString') ||
        isFeatureEnabled('doc-SxS-jsApi-ODB-Basecf') ||
        isFeatureEnabled('doc-SxS-jsApi-ODB-Readonly');

    const shouldShowLinkSurvey = isFeatureEnabled('doc-SxS-jsApi-ODB-Links');

    const resetPreviewPane = await lazyResetPreviewPane.import();
    const selectionSource = sxsStore.extendedViewState.selectionSource;

    resetPreviewPane(sxsStore.viewState);
    internalCloseSxS();
    onSxSChangeOrClose(null);
    repeatTracker.clear();
    sxsStore.eventManager.emit(CloseEvent, selectionSource);
    await lazyTriggerResizeEvent.importAndExecute();

    if (isWacAttachment) {
        if (
            shouldShowLinkSurvey &&
            (selectionSource == PreviewPaneSelectionSource.ReadingPaneDocLink ||
                selectionSource == PreviewPaneSelectionSource.ComposeDocLink)
        ) {
            logFloodgateActivity('LinkViewed');
        } else if (shouldShowAttachmentSurvey && isCloudyAttachment) {
            logFloodgateActivity('DocumentViewed');
        }
    }
}

orchestrator(forceCloseSxS, async actionMessage => {
    const { closingSource } = actionMessage;
    await forceCloseSxSOrchestrator(sxsStore, closingSource);
});
